/* import __COLOCATED_TEMPLATE__ from './meeting-booking-funnel.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import Component from '@glimmer/component';
import type AppService from 'embercom/services/app-service';
import type IntlService from 'ember-intl/services/intl';
import type {
  Chart,
  Options,
  Point,
  SVGElement as HighchartsSVGElement,
  SeriesBarOptions,
  SeriesOptionsType,
} from 'highcharts';
import PALETTE from '@intercom/pulse/lib/palette';
import { inject as service } from '@ember/service';
import { use } from 'ember-resources/util/function-resource';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { get } from 'embercom/lib/ajax';
import type { ReportState } from 'embercom/components/reporting/custom/report/chart-card';
import {
  getCardLayout,
  SdrMeetingBookingFunnelConfig,
  SUFFIXES,
} from 'embercom/components/reporting/bespoke/sdr-performance/meeting-booking-funnel/series-config';

type RoutingOutcome = {
  identifier: string;
  title: string;
  meeting_provider?: string | null;
};

type SdrConfig = {
  app_id: number;
  routing_outcomes: RoutingOutcome[];
};

interface Signature {
  Args: {
    reportState: ReportState;
  };
  Blocks: {
    default: [];
  };
  Element: null;
}

const SEGMENT_COLORS = {
  notBooked: PALETTE['neutral-border'],
  booked: PALETTE['vis-green-50'],
  buttonBooked: PALETTE['vis-green-30'],
};

// We stash the per-redraw badge SVGElements on the chart instance so the next render
// can destroy them before drawing fresh ones. Highcharts does not have a built-in
// "ephemeral overlay" lifecycle, so we manage it explicitly.
type ChartWithBadges = Chart & { _rowBadges?: HighchartsSVGElement[] };

// Stack indices defined by the highchartsSeries getter:
//   series[0] = notBooked, series[1] = organic, series[2] = button (omitted when 0).
// Badge color/count is derived from the live visibility of series[1] and series[2]:
//   - both visible (default)    -> count = organic + button, color = booked (vis-green-50)
//   - only button visible       -> count = button,           color = buttonBooked (vis-green-30)
//   - only organic visible      -> count = organic,          color = booked
//   - both hidden               -> badge omitted entirely
function renderRowBadges(chart: ChartWithBadges) {
  for (let prev of chart._rowBadges ?? []) {
    try {
      prev.destroy();
    } catch {
      // Element already destroyed during a previous teardown cycle — ignore.
    }
  }
  chart._rowBadges = [];

  let xAxis = chart.xAxis?.[0];
  let yAxis = chart.yAxis?.[0];
  if (!xAxis || !yAxis) {
    return;
  }
  let categories = xAxis.categories ?? [];
  if (categories.length === 0) {
    return;
  }

  let notBookedSeries = chart.series?.[0];
  let organicSeries = chart.series?.[1];
  let buttonSeries = chart.series?.[2];
  let notBookedVisible = notBookedSeries?.visible !== false;
  let organicVisible = organicSeries?.visible !== false;
  let buttonVisible = buttonSeries ? buttonSeries.visible !== false : false;

  if (!organicVisible && !buttonVisible) {
    return;
  }

  for (let idx = 0; idx < categories.length; idx++) {
    let notBooked = (notBookedSeries?.data?.[idx]?.y as number | null) ?? 0;
    let organic = (organicSeries?.data?.[idx]?.y as number | null) ?? 0;
    let button = (buttonSeries?.data?.[idx]?.y as number | null) ?? 0;

    let count: number;
    let color: string;
    if (organicVisible && buttonVisible) {
      count = organic + button;
      color = SEGMENT_COLORS.booked;
    } else if (buttonVisible) {
      count = button;
      color = SEGMENT_COLORS.buttonBooked;
    } else {
      count = organic;
      color = SEGMENT_COLORS.booked;
    }

    if (count > 0) {
      // Badge sits at the end of THIS row's bar. The bar end is the sum of all
      // currently-visible series values — so when the user toggles a legend item,
      // the chart redraws, this callback fires again, and the badge snaps to the
      // new bar-end position.
      let rowTotal =
        (notBookedVisible ? notBooked : 0) +
        (organicVisible ? organic : 0) +
        (buttonVisible ? button : 0);
      let xPosition = yAxis.toPixels(rowTotal, false) + 6;

      // Render first with a temporary y, then read getBBox() and reposition so the
      // label is geometrically centered with the bar's mid-line. Doing the math up
      // front would require hard-coding label height, which drifts when the SVG
      // text's ascent/descent or padding ever change.
      let badge = chart.renderer
        .label(count.toLocaleString(), xPosition, 0, 'rect', undefined, undefined, false)
        .attr({
          fill: color,
          padding: 4,
          r: 4,
          zIndex: 5,
        })
        .css({
          color: 'var(--text-default)',
          fontSize: '12px',
          fontWeight: '500',
        })
        .add();

      let yCenter = xAxis.toPixels(idx, false);
      let bbox = badge.getBBox();
      badge.attr({ y: yCenter - bbox.height / 2 });

      chart._rowBadges.push(badge);
    }
  }
}

export default class MeetingBookingFunnel extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;

  @tracked finSdrConfig: SdrConfig | null = null;
  @tracked configLoaded = false;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    taskFor(this.loadFinSdrConfig).perform();
  }

  @task({ restartable: true })
  *loadFinSdrConfig(): TaskGenerator<void> {
    try {
      let appId = this.appService.app.id;
      let response = (yield get(`/teammate_app/fin_sdr_config?app_id=${appId}`)) as SdrConfig;
      this.finSdrConfig = response;
    } catch (e: unknown) {
      console.error('[MeetingBookingFunnel] Failed to load SDR config:', e);
      this.finSdrConfig = null;
    } finally {
      this.configLoaded = true;
    }
  }

  get bookingEnabledOutcomes(): RoutingOutcome[] {
    let outcomes = this.finSdrConfig?.routing_outcomes || [];
    return outcomes.filter((o) => Boolean(o.meeting_provider));
  }

  get chartDefinition() {
    return {
      chart_series: SdrMeetingBookingFunnelConfig.getChartSeriesWithLabels(
        this.intl,
        this.bookingEnabledOutcomes,
      ),
      view_by: null,
    };
  }

  @use dataResource = staticDataResource(this, {
    serializedChart: () => this.chartDefinition,
    reportState: () => this.args.reportState,
  });

  get metrics(): Record<string, number> {
    return SdrMeetingBookingFunnelConfig.getMetricsFromChartDataRequest(
      this.dataResource.chartData,
      this.bookingEnabledOutcomes,
    );
  }

  get hasData() {
    return this.bookingEnabledOutcomes.some(
      (outcome) => (this.metrics[`${outcome.identifier}${SUFFIXES.total}`] || 0) > 0,
    );
  }

  get categories() {
    return this.bookingEnabledOutcomes.map((o) => o.title);
  }

  private metricFor(outcomeId: string, suffix: (typeof SUFFIXES)[keyof typeof SUFFIXES]): number {
    return this.metrics[`${outcomeId}${suffix}`] || 0;
  }

  get totalBooked() {
    return this.bookingEnabledOutcomes.reduce(
      (sum, o) => sum + this.metricFor(o.identifier, SUFFIXES.booked),
      0,
    );
  }

  get totalButtonBooked() {
    return this.bookingEnabledOutcomes.reduce(
      (sum, o) => sum + this.metricFor(o.identifier, SUFFIXES.buttonBooked),
      0,
    );
  }

  get buttonPercentage() {
    return this.totalBooked > 0 ? Math.round((this.totalButtonBooked / this.totalBooked) * 100) : 0;
  }

  get subtitleText() {
    if (this.totalButtonBooked > 0) {
      return this.intl.t(
        'ai-agent.analyze.sales-performance.meeting-booking-funnel.subtitle.with-button',
        {
          count: this.totalBooked,
          buttonCount: this.totalButtonBooked,
          pct: this.buttonPercentage,
        },
      );
    }
    return this.intl.t(
      'ai-agent.analyze.sales-performance.meeting-booking-funnel.subtitle.no-button',
      { count: this.totalBooked },
    );
  }

  get notBookedSeries(): SeriesBarOptions {
    let data = this.bookingEnabledOutcomes.map((outcome) => {
      let total = this.metricFor(outcome.identifier, SUFFIXES.total);
      let booked = this.metricFor(outcome.identifier, SUFFIXES.booked);
      let notBooked = Math.max(total - booked, 0);
      return total > 0 ? notBooked : null;
    });
    return {
      type: 'bar',
      name: this.intl.t(
        'ai-agent.analyze.sales-performance.meeting-booking-funnel.legend.not-booked',
      ),
      data,
      color: SEGMENT_COLORS.notBooked,
    };
  }

  get bookedOrganicSeries(): SeriesBarOptions {
    let data = this.bookingEnabledOutcomes.map((outcome) => {
      let booked = this.metricFor(outcome.identifier, SUFFIXES.booked);
      let buttonBooked = this.metricFor(outcome.identifier, SUFFIXES.buttonBooked);
      let organic = Math.max(booked - buttonBooked, 0);
      if (organic > 0) {
        return organic;
      }
      return booked > 0 ? 0 : null;
    });
    return {
      type: 'bar',
      name: this.intl.t(
        'ai-agent.analyze.sales-performance.meeting-booking-funnel.legend.total-booked',
      ),
      data,
      color: SEGMENT_COLORS.booked,
    };
  }

  get bookedViaButtonSeries(): SeriesBarOptions {
    let data = this.bookingEnabledOutcomes.map((outcome) => {
      let buttonBooked = this.metricFor(outcome.identifier, SUFFIXES.buttonBooked);
      return buttonBooked > 0 ? buttonBooked : null;
    });
    return {
      type: 'bar',
      name: this.intl.t(
        'ai-agent.analyze.sales-performance.meeting-booking-funnel.legend.booked-via-button',
      ),
      data,
      color: SEGMENT_COLORS.buttonBooked,
    };
  }

  // Stack order is significant: notBooked is series[0] so it renders at the start of each
  // horizontal bar, the organic-booked subset of "Total meetings booked" sits in the middle,
  // and the button-attributed portion is the lighter cap at the end.
  // The button series is omitted entirely when there are no button bookings — this removes
  // its legend entry (the "filter") and keeps the badge in permanent total mode.
  get highchartsSeries(): SeriesOptionsType[] {
    let series: SeriesOptionsType[] = [this.notBookedSeries, this.bookedOrganicSeries];
    if (this.totalButtonBooked > 0) {
      series.push(this.bookedViaButtonSeries);
    }
    return series;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  get highchartsOptions(): Options {
    // Card height (parent's grid_height) and chart height stay in sync via getCardLayout —
    // see series-config.ts for the formula and rationale.
    let { chartPx } = getCardLayout(this.bookingEnabledOutcomes.length);

    let component = this;
    let tooltipLabels = {
      total: this.intl.t('ai-agent.analyze.sales-performance.meeting-booking-funnel.tooltip.total'),
      qualification: this.intl.t(
        'ai-agent.analyze.sales-performance.meeting-booking-funnel.tooltip.qualification',
      ),
      button: this.intl.t(
        'ai-agent.analyze.sales-performance.meeting-booking-funnel.tooltip.button',
      ),
    };

    return {
      chart: {
        type: 'bar',
        height: chartPx,
        animation: false,
        marginLeft: 150,
        // Reserved so the end-of-row badge (rendered by `renderRowBadges`) has space
        // past the bar's right edge without clipping when a row's total is at yAxis.max.
        marginRight: 50,
        spacingTop: 8,
        backgroundColor: 'transparent',
        events: {
          // Highcharts fires `render` after every full redraw — including the implicit
          // redraw that follows a legend item click. We use it to draw end-of-row count
          // badges via `renderer.label()` so they live-update with series visibility. The
          // linked-axis `opposite: true` approach turned out unreliable for category axes
          // in bar charts (labels aren't independently re-emitted), so we render badges
          // directly onto the SVG.
          render(this: Chart) {
            renderRowBadges(this);
          },
        },
      },
      title: { text: undefined },
      xAxis: {
        categories: this.categories,
        labels: {
          useHTML: true,
          align: 'right',
          style: { color: 'var(--text-default)', fontSize: '12px', fontWeight: '500' },
          formatter(this: { value: string | number }) {
            let title = String(this.value);
            return `<span class="text-default text-caption">${component.escapeHtml(title)}</span>`;
          },
        },
        lineColor: 'var(--neutral-border)',
      },
      yAxis: { visible: false },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        layout: 'horizontal',
        symbolRadius: 4,
        // Highcharts does not inherit itemStyle.color into hover/hidden states; set all three
        // explicitly so legend text stays readable across themes and toggle states.
        itemStyle: { color: 'var(--text-default)', fontSize: '12px', fontWeight: '400' },
        itemHoverStyle: { color: 'var(--text-muted)' },
        itemHiddenStyle: { color: 'var(--text-disabled)' },
      },
      tooltip: {
        useHTML: true,
        outside: true,
        shared: true,
        backgroundColor: 'var(--base-module)',
        borderColor: 'var(--neutral-border)',
        borderRadius: 8,
        borderWidth: 1,
        shadow: { color: 'rgba(0,0,0,0.08)', offsetX: 0, offsetY: 2, width: 8 },
        padding: 12,
        style: { color: 'var(--text-default)', fontSize: '13px' },
        formatter(this: Point) {
          let points = this.points ?? [this];
          let chart = points[0]?.series?.chart;
          let idx = points[0]?.index ?? 0;
          if (!chart) {
            return '';
          }
          let organic: number = (chart.series[1]?.data[idx]?.y as number | null) ?? 0;
          let button: number = (chart.series[2]?.data[idx]?.y as number | null) ?? 0;
          let total = organic + button;
          let title = chart.xAxis[0].categories?.[idx] ?? '';

          let row = (label: string, value: number, swatch: string) => `
            <div style="display:flex;align-items:center;gap:8px;padding-top:4px;">
              <span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${swatch};"></span>
              <span style="flex:1;color:var(--text-muted);font-size:12px;">${label}</span>
              <span style="font-weight:600;color:var(--text-default);font-size:12px;">${value.toLocaleString()}</span>
            </div>`;

          return `<div style="min-width:200px;">
            <div style="font:600 13px/1.3 Inter,system-ui,sans-serif;color:var(--text-default);">${component.escapeHtml(title)}</div>
            <div style="font:500 11px/1.3 Inter,system-ui,sans-serif;color:var(--text-muted);padding-bottom:6px;">${tooltipLabels.total}: ${total.toLocaleString()}</div>
            ${row(tooltipLabels.qualification, organic, SEGMENT_COLORS.booked)}
            ${row(tooltipLabels.button, button, SEGMENT_COLORS.buttonBooked)}
          </div>`;
        },
      },
      plotOptions: {
        bar: {
          stacking: 'normal',
          borderRadius: 3,
          pointWidth: 20,
          states: {
            hover: { brightness: -0.05 },
            inactive: { enabled: false },
          },
          dataLabels: {
            enabled: false,
          },
        },
      },
      credits: { enabled: false },
    };
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Bespoke::SdrPerformance::MeetingBookingFunnel': typeof MeetingBookingFunnel;
    'reporting/bespoke/sdr-performance/meeting-booking-funnel': typeof MeetingBookingFunnel;
  }
}
